import {
  useQuery
} from "@tanstack/react-query";

import {
  Quote,
  Star
} from "lucide-react";

import {
  getFeaturedTestimonials,
  getPublicTestimonialImageUrl
} from "../../services/catalog";

import {
  findHomeSection,
  useHomeSections
} from "./HomeSectionCms";

export function CmsTestimonialsSection() {
  const sectionsQuery =
    useHomeSections();

  const testimonialsQuery =
    useQuery({
      queryKey: [
        "homepage",
        "testimonials"
      ],

      queryFn:
        getFeaturedTestimonials,

      staleTime: 0,

      refetchOnMount:
        "always",

      refetchOnWindowFocus:
        true
    });

  const section =
    findHomeSection(
      sectionsQuery.data,
      "TESTIMONIALS"
    );

  if (
    section &&
    !section.isVisible
  ) {
    return null;
  }

  const testimonials =
    testimonialsQuery.data ??
    [];

  if (
    !testimonialsQuery.isLoading &&
    testimonials.length === 0
  ) {
    return null;
  }

  const eyebrow =
    section?.eyebrow ??
    "Customer love";

  const title =
    section?.title ??
    "Kind words from the RiNi community";

  const subtitle =
    section?.subtitle ??
    "Real experiences shared by customers who make RiNi part of their self-care routine.";

  return (
    <section className="rini-section bg-[#f7f1e7]">
      <div className="rini-container">
        <div className="mx-auto max-w-3xl text-center">
          {eyebrow && (
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
              {eyebrow}
            </p>
          )}

          <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
            {title}
          </h2>

          {subtitle && (
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-[#746d65]">
              {subtitle}
            </p>
          )}
        </div>

        {testimonialsQuery.isLoading ? (
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {Array.from({
              length: 3
            }).map(
              (_item, index) => (
                <div
                  key={index}
                  className="h-80 animate-pulse rounded-[2rem] bg-white/70"
                />
              )
            )}
          </div>
        ) : (
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {testimonials.map(
              (testimonial) => (
                <article
                  key={
                    testimonial.id
                  }
                  className="flex h-full flex-col rounded-[2rem] bg-white p-7 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-4">
                    <Quote
                      size={32}
                      className="text-[#c59a63]"
                      strokeWidth={
                        1.5
                      }
                    />

                    <div className="flex gap-1 text-[#c59a63]">
                      {Array.from({
                        length:
                          testimonial.rating
                      }).map(
                        (_item, index) => (
                          <Star
                            key={index}
                            size={15}
                            fill="currentColor"
                          />
                        )
                      )}
                    </div>
                  </div>

                  <p className="mt-6 flex-1 font-serif text-2xl leading-9 text-[#38251e]">
                    “{
                      testimonial.reviewText
                    }”
                  </p>

                  <div className="mt-7 flex items-center gap-4 border-t border-[#5e3b2c]/10 pt-5">
                    {testimonial.imageKey ? (
                      <img
                        src={
                          getPublicTestimonialImageUrl(
                            testimonial.id,
                            testimonial.imageKey
                          )
                        }
                        alt={
                          testimonial.customerName
                        }
                        loading="lazy"
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#edf1e8] font-serif text-lg font-semibold text-[#70845b]">
                        {
                          testimonial.customerName
                            .charAt(0)
                            .toUpperCase()
                        }
                      </div>
                    )}

                    <div>
                      <p className="font-semibold text-[#5e3b2c]">
                        {
                          testimonial.customerName
                        }
                      </p>

                      <p className="mt-1 text-sm text-[#746d65]">
                        {
                          testimonial.location ??
                          "RiNi customer"
                        }
                      </p>
                    </div>
                  </div>
                </article>
              )
            )}
          </div>
        )}
      </div>
    </section>
  );
}
